// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { scroller, Element } from 'react-scroll';
import { FormattedNumber } from 'react-intl';

// Components
import LayerHeading from './LayerHeading';
import SearchControl from './SearchControl';
import { DescriptionContent } from './PlaceDescription';
import HtmlText from '../../shared/components/HtmlText';

// Actions
import { selectFeature } from '../actions';

// Shapes & Types
import { layerShape, featureShape } from '../propTypesShapes';
import { profileShape } from '../../authentication/propTypesShapes';

// Assets
import './OrganizationsList.css';

// Utils
import { isFirstRender } from '../../shared/utils/data';
import { isDefined } from '../../shared/utils/is';
import { metersToMiles } from '../utils/map';

// Constants
import { LEFT_PANE } from '../constants';


class OrganizationsList extends React.PureComponent {
  /* Not use withRouter and pass the location property due to
     https://github.com/ReactTraining/react-router/blob/v4.2.2/packages/
     react-router/docs/guides/blocked-updates.md */
  static propTypes = {
    token: PropTypes.string,
    profile: profileShape,
    location: PropTypes.object,
    history: PropTypes.object,
    currentLayer: layerShape,
    currentFeature: featureShape
  };

  constructor(props) {
    super(props);
    const { currentLayer, currentFeature } = props;
    let firstTierStatus = null;
    let hasSelectedAnOrganization = false;

    if (currentLayer && !isFirstRender(currentLayer.dataLayer)) {
      const { features } = currentLayer.dataLayer;
      firstTierStatus = {};

      features.forEach(organization => {
        const selected = isDefined(currentFeature) && organization.id === currentFeature.id;
        hasSelectedAnOrganization = hasSelectedAnOrganization || selected;

        firstTierStatus[organization.id] = {
          hasDetails: true,
          expanded: false,
          selected
        };
      });
    }

    this.state = { firstTierStatus, hasSelectedAnOrganization };

    this.handleFirstTierOnClick = this.handleFirstTierOnClick.bind(this);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { currentLayer, currentFeature } = newProps;

    if (currentLayer !== this.props.currentLayer && currentLayer && !isFirstRender(currentLayer.dataLayer)) {
      const { features } = currentLayer.dataLayer;
      const firstTierStatus = {};
      let hasSelectedAnOrganization = false;

      features.forEach(organization => {
        const selected = isDefined(currentFeature) && organization.id === currentFeature.id;
        hasSelectedAnOrganization = hasSelectedAnOrganization || selected;

        firstTierStatus[organization.id] = {
          hasDetails: true,
          expanded: false,
          selected
        };
      });

      this.setState({ firstTierStatus, hasSelectedAnOrganization });
    }

    if (currentFeature !== this.props.currentFeature) {
      const organizationId = currentFeature ? currentFeature.id : null;
      this.selectOrganization(organizationId);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { currentFeature, location } = this.props;
    const { firstTierStatus } = this.state;

    if ((firstTierStatus !== prevState.firstTierStatus ||
      (location.state && location.state !== prevProps.location.state
        && location.state.fromBottomMenuBar && location.state.selectedListOption))
      && currentFeature) {
      //Calculate the offset from top of viewport until the start of container
      //const bodyRect = document.body.getBoundingClientRect();
      const containerRect = document.getElementById('ScrollableContainer').getBoundingClientRect();
      const offset = containerRect.top; // - bodyRect.top;

      scroller.scrollTo(`option${currentFeature.id}`, {
        duration: 800,
        delay: 0,
        offset: -offset,
        smooth: 'easeInOutQuart',
        containerId: 'ScrollableContainer'
      });
    }
  }

  selectOrganization(organizationId) {
    const { firstTierStatus } = this.state;
    const newFirstTierStatus = { ...firstTierStatus };
    let hasSelectedAnOrganization = false;

    Object.keys(newFirstTierStatus).forEach(orgId => {
      const selected = isDefined(organizationId) && orgId === organizationId;
      hasSelectedAnOrganization = hasSelectedAnOrganization || selected;

      newFirstTierStatus[orgId] = {
        ...firstTierStatus[orgId],
        selected,
        expanded: false
      };
    });

    this.setState({ firstTierStatus: newFirstTierStatus, hasSelectedAnOrganization });
  }

  handleFirstTierOnClick(organizationId) {
    const { firstTierStatus } = this.state;
    const newFirstTierStatus = { ...firstTierStatus };

    if (firstTierStatus[organizationId].selected) {
      Object.keys(newFirstTierStatus).forEach(orgId => {
        newFirstTierStatus[orgId] = {
          ...firstTierStatus[orgId],
          expanded: orgId === organizationId
            ? !firstTierStatus[orgId].expanded
            : false
        };
      });

      this.setState({ firstTierStatus: newFirstTierStatus });
    } else {
      const { token, profile, currentLayer, dispatch } = this.props;
      const feature = currentLayer.dataLayer.features.find(f => f.id === organizationId);

      this.selectOrganization(organizationId);
      dispatch(selectFeature(token, profile, currentLayer.mapLayer, feature));
    }
  }

  renderFeaturePreview(feature) {
    const {
      Address,
      Postcode,
      'Type of Business': typeOfBusiness,
      County,
      'OFFICE PHONE': officePhone,
      'MOBILE PHONE': mobilePhone
    } = feature.properties;
    const propsToRender = [];

    // TODO: show the properties based upon the customer account. For now,
    // there are no properties overlapped among the accounts.

    // This comes in LITS Kenya
    if (typeOfBusiness) {
      propsToRender.push(typeOfBusiness);
    }

    // Veterans Gateway & TAFCFT, and some locations in LITS Kenya
    if (Address) {
      propsToRender.push(Postcode ? `${Address} ${Postcode}` : Address);
    }

    // This comes in LITS Kenya
    if (County) {
      propsToRender.push(County);
    }

    // This comes from Real Estate
    if (officePhone) {
      propsToRender.push(officePhone);
    }
    if (mobilePhone) {
      propsToRender.push(mobilePhone);
    }


    return propsToRender.map((prop, index) => (
      <>
        {index !== 0 ? <br /> : null}
        <HtmlText text={prop} />
      </>
    ));
  }

  renderFeature(feature) {
    const { firstTierStatus } = this.state;

    if (isFirstRender(firstTierStatus)) {
      return null;
    }

    const { id, properties, distanceFromUser } = feature;
    const { profile } = this.props;
    const { name } = properties;
    const distanceFromUserInMiles = distanceFromUser ? metersToMiles(distanceFromUser) : false;

    return (
      <li className="MenuNavOption" key={id}>
        <Element
          className={`MenuOptionLink ${firstTierStatus[id].hasDetails ? 'icon-arrow' : ''}`}
          name={`option${id}`}
          data-toggle="collapse"
          aria-expanded={firstTierStatus[id].expanded}
          aria-selected={firstTierStatus[id].selected}
          onClick={() => this.handleFirstTierOnClick(id)}
        >
          <div className="d-flex justify-content-between">
            <div>
              <span>{name}</span>
              <p>{this.renderFeaturePreview(feature)}</p>
            </div>
            {distanceFromUserInMiles &&
              <span className="DistanceFromUser" title="Approximate distance from your location">
                <React.Fragment>
                  <FormattedNumber
                    value={distanceFromUserInMiles}
                    minimumFractionDigits={0}
                    maximumFractionDigits={1}
                  /> miles
                </React.Fragment>
              </span>
            }
          </div>
        </Element>

        {firstTierStatus[id].hasDetails &&
          <ul className={`MenuTierContainer collapse list-unstyled ${
            firstTierStatus[id].expanded ? 'show' : ''}`}
          >
            <li className="MenuNavOption">
              <div className="MenuOptionContent">
                <DescriptionContent
                  profile={profile}
                  featureProperties={properties}
                />
              </div>
            </li>
          </ul>
        }
      </li>
    );
  }

  render() {
    const { currentLayer, location, history } = this.props;
    const { hasSelectedAnOrganization } = this.state;
    const dataLayer = currentLayer ? currentLayer.dataLayer : null;
    const showSearchControl = location.search === LEFT_PANE.SEARCH_LINK.LISTING;

    return (
      <div className='OrganizationsList d-flex flex-column h-100'>
        <LayerHeading hideResultsInfo={showSearchControl} />

        {showSearchControl ? (
          <SearchControl location={location} history={history} />
        ) : (
          <nav className={`MenuNavBarContainer ${hasSelectedAnOrganization ? 'TopDivider' : ''}`}
            id="ScrollableContainer"
          >
            {dataLayer &&
              <ul className="MenuNavBar list-unstyled">
                {dataLayer.features.map((feature) =>
                  this.renderFeature(feature)
                )}
              </ul>
            }
          </nav>
        )}
      </div>
    );
  }
}

export default connect(({ authentication, home }) => ({
  currentLayer: home.currentLayer,
  currentFeature: home.currentFeature,
  token: authentication.token,
  profile: authentication.profile
}), null)(OrganizationsList);
